import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import WelcomeModal from '../components/WelcomeModal';
import { apiGet, apiGetText, apiPost, apiPut, apiDelete, ApiError } from '../api/client';
import type {
  AdminDashboardResponse,
  AdminUserResponse,
  AdminUserDetailResponse,
  AdminVaultResponse,
  AdminVaultDetailResponse,
  AdminActivityResponse,
  AdminInvitationResponse,
  ForeverFreeInvitationResponse,
  TermsVersionSummary,
  TermsAndConditionsResponse,
  TermsAcceptanceAuditEntry,
  CreateTermsRequest,
  SaveAdminNotesRequest,
  AdminMessageRequest,
  AdminHealthResponse,
  AdminMaintenanceStepResult,
} from '../types/api';
import { VaultStatus, VaultRole, AccountTier } from '../types/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './AdminPage.css';
import { fireConfetti } from '../utils/confetti';

type Tab = 'dashboard' | 'users' | 'vaults' | 'invitations' | 'forever-free' | 'activity' | 'health' | 'emails' | 'terms';

export default function AdminPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [devFeaturesEnabled, setDevFeaturesEnabled] = useState(false);

  useEffect(() => {
    apiGet<AdminDashboardResponse>('/api/admin/dashboard')
      .then(d => setDevFeaturesEnabled(d.devFeaturesEnabled))
      .catch(() => {});
  }, []);

  if (!user?.isAdmin) {
    return <div className="admin-empty">Not authorized.</div>;
  }

  return (
    <div className="admin-page">
      <h1 className="admin-title">Admin Console</h1>
      <div className="admin-tabs">
        {(['dashboard', 'users', 'vaults', 'invitations', 'forever-free', 'activity', 'health', 'emails', 'terms'] as Tab[]).map(tab => (
          <button
            key={tab}
            className={`admin-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'forever-free' ? 'Forever Free' : tab === 'emails' ? 'Emails' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <DashboardTab onNavigate={(tab) => setActiveTab(tab)} />}
      {activeTab === 'users' && <UsersTab devFeaturesEnabled={devFeaturesEnabled} />}
      {activeTab === 'vaults' && <VaultsTab />}
      {activeTab === 'invitations' && <InvitationsTab />}
      {activeTab === 'forever-free' && <ForeverFreeTab />}
      {activeTab === 'activity' && <ActivityTab />}
      {activeTab === 'health' && <HealthTab />}
      {activeTab === 'emails' && <EmailsTab />}
      {activeTab === 'terms' && <TermsTab />}
    </div>
  );
}

function DashboardTab({ onNavigate }: { onNavigate: (tab: Tab) => void }) {
  const [data, setData] = useState<AdminDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiGet<AdminDashboardResponse>('/api/admin/dashboard')
      .then(d => {
        setData(d);
        const stored = Number(localStorage.getItem('cv_admin_lastPaidVaults') ?? '0');
        if (d.paidVaults > stored) fireConfetti('big');
        localStorage.setItem('cv_admin_lastPaidVaults', String(d.paidVaults));
      })
      .catch((err) => setError(err.message || 'Failed to load dashboard.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="admin-empty">{error}</div>;
  if (!data) return <div className="admin-empty">Failed to load dashboard.</div>;

  const fmt$ = (n: number) =>
    n === 0 ? '$0' : '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const actionItems: {
    key: string;
    count: number;
    label: string;
    sublabel: string;
    urgency: 'red' | 'amber' | 'green';
    tab: Tab;
    icon: string;
  }[] = [
    {
      key: 'locked',
      count: data.lockedOutUsers,
      label: 'Locked Out',
      sublabel: 'accounts locked — users can\'t sign in',
      urgency: data.lockedOutUsers > 0 ? 'red' : 'green',
      tab: 'users',
      icon: '🔒',
    },
    {
      key: 'unconfirmed',
      count: data.unconfirmedEmailUsers,
      label: 'Unconfirmed Emails',
      sublabel: 'registered but email not yet confirmed',
      urgency: data.unconfirmedEmailUsers > 3 ? 'amber' : data.unconfirmedEmailUsers > 0 ? 'amber' : 'green',
      tab: 'users',
      icon: '✉️',
    },
    {
      key: 'trials',
      count: data.trialsExpiringIn7Days,
      label: 'Trials Expiring',
      sublabel: 'active trials ending within 7 days',
      urgency: data.trialsExpiringIn7Days > 0 ? 'amber' : 'green',
      tab: 'vaults',
      icon: '⏳',
    },
    {
      key: 'readonly',
      count: data.readOnlyVaults,
      label: 'Read-Only Vaults',
      sublabel: 'vaults awaiting payment or admin action',
      urgency: data.readOnlyVaults > 0 ? 'amber' : 'green',
      tab: 'vaults',
      icon: '🔐',
    },
    {
      key: 'stale',
      count: data.staleInvitations,
      label: 'Stale Invitations',
      sublabel: 'pending invites older than 7 days',
      urgency: data.staleInvitations > 0 ? 'amber' : 'green',
      tab: 'invitations',
      icon: '📨',
    },
  ];

  const allClear = actionItems.every(i => i.count === 0);

  return (
    <div className="admin-dashboard">

      {/* ── Revenue Banner ── */}
      <div className="dash-revenue-banner">
        <div className="dash-revenue-group">
          <span className="dash-revenue-group-label">Real</span>
          <div className="dash-revenue-group-cells">
            <div className="dash-revenue-item">
              <span className="dash-revenue-value">{fmt$(data.monthlyRevenue)}</span>
              <span className="dash-revenue-label">MRR</span>
            </div>
            <div className="dash-revenue-divider" />
            <div className="dash-revenue-item">
              <span className="dash-revenue-value">{fmt$(data.annualRunRate)}</span>
              <span className="dash-revenue-label">ARR</span>
            </div>
          </div>
        </div>
        <div className="dash-revenue-group-divider" />
        <div className="dash-revenue-group">
          <span className="dash-revenue-group-label">If {data.activeTrialVaults} Trial{data.activeTrialVaults !== 1 ? 's' : ''} Convert</span>
          <div className="dash-revenue-group-cells">
            <div className="dash-revenue-item">
              <span className="dash-revenue-value dash-revenue-value--muted">{fmt$(data.trialConvertedMrr)}</span>
              <span className="dash-revenue-label">MRR</span>
            </div>
            <div className="dash-revenue-divider" />
            <div className="dash-revenue-item">
              <span className="dash-revenue-value dash-revenue-value--muted">{fmt$(data.trialConvertedArr)}</span>
              <span className="dash-revenue-label">ARR</span>
            </div>
          </div>
        </div>
        <div className="dash-revenue-group-divider" />
        <button
          onClick={() => fireConfetti('big')}
          style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', padding: '0 4px', opacity: 0.6, lineHeight: 1 }}
          title="Just because 🎉"
        >🎉</button>
      </div>

      {/* ── Action Queue ── */}
      <div className="dash-section">
        <div className="dash-section-header">
          <h3 className="dash-section-title">Action Queue</h3>
          <span className="dash-section-subtitle">Items that may need your attention</span>
        </div>

        {allClear ? (
          <div className="dash-all-clear">
            <span className="dash-all-clear-icon">✓</span>
            <div>
              <div className="dash-all-clear-title">All clear</div>
              <div className="dash-all-clear-sub">No locked accounts, expiring trials, or stale invitations.</div>
            </div>
          </div>
        ) : (
          <div className="dash-action-grid">
            {actionItems.map(item => (
              <div key={item.key} className={`dash-action-card dash-action-card--${item.urgency}`}>
                <div className="dash-action-card-top">
                  <span className="dash-action-icon">{item.icon}</span>
                  <span className={`dash-action-count dash-action-count--${item.urgency}`}>{item.count}</span>
                </div>
                <div className="dash-action-label">{item.label}</div>
                <div className="dash-action-sublabel">{item.sublabel}</div>
                <button
                  className="dash-action-btn"
                  onClick={() => onNavigate(item.tab)}
                  disabled={item.count === 0}
                >
                  {item.count > 0 ? 'Handle it →' : 'All good ✓'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Platform Grid ── */}
      <div className="dash-grid-3">

        {/* Vault Tiers */}
        <div className="dash-card">
          <h4 className="dash-card-title">Vault Tiers</h4>
          <table className="dash-metric-table">
            <tbody>
              <tr>
                <td className="dash-metric-label">Trial</td>
                <td className="dash-metric-value">{data.trialVaults}</td>
                <td className="dash-metric-badge dash-metric-badge--amber">active</td>
              </tr>
              <tr>
                <td className="dash-metric-label">Paid</td>
                <td className="dash-metric-value">{data.paidVaults}</td>
                <td className="dash-metric-badge dash-metric-badge--green">billing</td>
              </tr>
              <tr>
                <td className="dash-metric-label">Forever Free</td>
                <td className="dash-metric-value">{data.foreverFreeVaults}</td>
                <td className="dash-metric-badge dash-metric-badge--blue">gifted</td>
              </tr>
              <tr className="dash-metric-divider-row">
                <td className="dash-metric-label">Read-Only</td>
                <td className="dash-metric-value">{data.readOnlyVaults}</td>
                <td className="dash-metric-badge dash-metric-badge--red">blocked</td>
              </tr>
              <tr>
                <td className="dash-metric-label">Suspended</td>
                <td className="dash-metric-value">{data.suspendedVaults}</td>
                <td className="dash-metric-badge dash-metric-badge--red">admin</td>
              </tr>
              <tr className="dash-metric-total-row">
                <td className="dash-metric-label">Total Vaults</td>
                <td className="dash-metric-value dash-metric-value--total">{data.totalVaults}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Members */}
        <div className="dash-card">
          <h4 className="dash-card-title">Members</h4>
          <table className="dash-metric-table">
            <tbody>
              <tr>
                <td className="dash-metric-label">Owners</td>
                <td className="dash-metric-value">{data.totalOwners}</td>
                <td className="dash-metric-badge dash-metric-badge--gold">vault</td>
              </tr>
              <tr>
                <td className="dash-metric-label">Spouses</td>
                <td className="dash-metric-value">{data.totalSpouses}</td>
                <td className="dash-metric-badge dash-metric-badge--blue">co-edit</td>
              </tr>
              <tr>
                <td className="dash-metric-label">Witnesses</td>
                <td className="dash-metric-value">{data.totalWitnesses}</td>
                <td className="dash-metric-badge dash-metric-badge--blue">read</td>
              </tr>
              <tr className="dash-metric-divider-row">
                <td className="dash-metric-label">New this week</td>
                <td className="dash-metric-value">{data.newUsersThisWeek}</td>
                <td className="dash-metric-badge dash-metric-badge--green">↑</td>
              </tr>
              <tr>
                <td className="dash-metric-label">New this month</td>
                <td className="dash-metric-value">{data.newUsersThisMonth}</td>
                <td className="dash-metric-badge dash-metric-badge--green">↑</td>
              </tr>
              <tr className="dash-metric-total-row">
                <td className="dash-metric-label">Total Users</td>
                <td className="dash-metric-value dash-metric-value--total">{data.totalUsers}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Content */}
        <div className="dash-card">
          <h4 className="dash-card-title">Content Stored</h4>
          <table className="dash-metric-table">
            <tbody>
              <tr>
                <td className="dash-metric-label">Documents</td>
                <td className="dash-metric-value">{data.totalDocuments}</td>
                <td></td>
              </tr>
              <tr>
                <td className="dash-metric-label">Heirlooms</td>
                <td className="dash-metric-value">{data.totalHeirlooms}</td>
                <td></td>
              </tr>
              <tr>
                <td className="dash-metric-label">Private Notes</td>
                <td className="dash-metric-value">{data.totalNotes}</td>
                <td></td>
              </tr>
              <tr>
                <td className="dash-metric-label">Emergency Contacts</td>
                <td className="dash-metric-value">{data.totalContacts}</td>
                <td></td>
              </tr>
              <tr>
                <td className="dash-metric-label">Funeral Plans</td>
                <td className="dash-metric-value">{data.totalFuneralPlans}</td>
                <td></td>
              </tr>
              <tr>
                <td className="dash-metric-label">Legacy Letters</td>
                <td className="dash-metric-value">{data.totalLetters}</td>
                <td></td>
              </tr>
              <tr>
                <td className="dash-metric-label">Legacy Videos</td>
                <td className="dash-metric-value">{data.totalVideos}</td>
                <td></td>
              </tr>
              <tr className="dash-metric-divider-row">
                <td className="dash-metric-label">Pending Invites</td>
                <td className="dash-metric-value">{data.pendingInvitations}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}


function UsersTab({ devFeaturesEnabled }: { devFeaturesEnabled: boolean }) {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState<AdminUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<AdminUserDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const load = useCallback((q?: string) => {
    setLoading(true);
    const url = q ? `/api/admin/users?search=${encodeURIComponent(q)}` : '/api/admin/users';
    apiGet<AdminUserResponse[]>(url)
      .then(setUsers)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    load(search);
  }

  async function toggleExpand(userId: string) {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setUserDetail(null);
      setActionMessage(null);
      return;
    }
    setExpandedUserId(userId);
    setUserDetail(null);
    setActionMessage(null);
    setDetailLoading(true);
    try {
      const detail = await apiGet<AdminUserDetailResponse>(`/api/admin/users/${userId}`);
      setUserDetail(detail);
    } catch {
      setUserDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function refreshDetail(userId: string) {
    try {
      const detail = await apiGet<AdminUserDetailResponse>(`/api/admin/users/${userId}`);
      setUserDetail(detail);
    } catch {}
  }

  async function handleUnlock(userId: string) {
    if (!window.confirm('Unlock this account? The user will be able to attempt login again.')) return;
    try {
      await apiPost(`/api/admin/users/${userId}/unlock`);
      setActionMessage({ text: 'Account unlocked.', type: 'success' });
      await refreshDetail(userId);
    } catch (err: any) {
      setActionMessage({ text: err.message || 'Unlock failed.', type: 'error' });
    }
  }

  async function handleDisable2FA(userId: string) {
    if (!window.confirm('Disable 2FA for this user? They will need to set it up again on next login.')) return;
    try {
      await apiPost(`/api/admin/users/${userId}/disable-2fa`);
      setActionMessage({ text: '2FA disabled. User must re-setup on next login.', type: 'success' });
      await refreshDetail(userId);
      load(search);
    } catch (err: any) {
      setActionMessage({ text: err.message || 'Failed to disable 2FA.', type: 'error' });
    }
  }

  async function handleConfirmEmail(userId: string, email: string) {
    if (!window.confirm(`Manually confirm email for ${email}?`)) return;
    try {
      await apiPost(`/api/admin/users/${userId}/confirm-email`);
      setActionMessage({ text: `Email confirmed for ${email}.`, type: 'success' });
      await refreshDetail(userId);
    } catch (err: any) {
      setActionMessage({ text: err.message || 'Failed to confirm email.', type: 'error' });
    }
  }

  async function handleSendPasswordReset(userId: string, email: string) {
    if (!window.confirm(`Send a password reset email to ${email}?`)) return;
    try {
      await apiPost(`/api/admin/users/${userId}/send-password-reset`);
      setActionMessage({ text: `Password reset email sent to ${email}.`, type: 'success' });
    } catch (err: any) {
      setActionMessage({ text: err.message || 'Failed to send password reset.', type: 'error' });
    }
  }

  async function handleResetMyAccount() {
    const msg =
      'RESET MY ACCOUNT FOR E2E TESTING\n\n' +
      'This will permanently delete your vault, all its contents, and your user account.\n\n' +
      'You will be signed out and must register again from scratch.\n\n' +
      'Are you absolutely sure?';
    if (!window.confirm(msg)) return;
    try {
      await apiDelete('/api/dev/reset-my-account');
    } catch (err) {
      if (err instanceof ApiError) {
        setActionMessage({ text: (err as ApiError).message || 'Reset failed.', type: 'error' });
        return;
      }
      // SyntaxError = got a 200 but response wasn't JSON — reset succeeded, safe to logout.
    }
    await logout();
  }

  async function handleDeleteUser(u: AdminUserResponse) {
    const vaultWarning = u.vaultId
      ? `\n\nThis user owns a vault ("${u.vaultName}") \u2014 it will also be permanently deleted along with all its contents.`
      : '';
    const msg = `Delete user "${u.email}"?${vaultWarning}\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await apiDelete(`/api/admin/users/${u.userId}`);
      setExpandedUserId(null);
      setUserDetail(null);
      load(search);
    } catch (err: any) {
      setActionMessage({ text: err.message || 'Delete failed.', type: 'error' });
    }
  }

  const roleLabel = (role: VaultRole | null): string => {
    if (role === null) return '-';
    switch (role) {
      case VaultRole.Owner: return 'Owner';
      case VaultRole.Spouse: return 'Spouse';
      case VaultRole.Witness: return 'Witness';
      default: return '-';
    }
  };

  return (
    <div>
      <form onSubmit={handleSearch}>
        <input
          type="text"
          className="admin-search"
          placeholder="Search by email or name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </form>
      {loading ? <LoadingSpinner /> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Vaults</th>
                <th>Registered</th>
                <th>Last Login</th>
                <th>2FA</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <React.Fragment key={u.userId}>
                  <tr
                    className={`admin-table-row-clickable${expandedUserId === u.userId ? ' admin-table-row-expanded' : ''}`}
                    onClick={() => toggleExpand(u.userId)}
                  >
                    <td>{u.email}</td>
                    <td>{u.firstName} {u.lastName}</td>
                    <td>
                      <span className={`admin-status-badge ${u.membershipCount > 0 ? 'admin-status-badge--ok' : 'admin-status-badge--muted'}`}>
                        {u.membershipCount}
                      </span>
                    </td>
                    <td>{formatDate(u.createdAt)}</td>
                    <td>{u.lastLoginAt ? formatDate(u.lastLoginAt) : '-'}</td>
                    <td>
                      <span className={`admin-2fa ${u.has2FA ? 'admin-2fa-yes' : 'admin-2fa-no'}`}>
                        {u.has2FA ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                  {expandedUserId === u.userId && (
                    <tr>
                      <td colSpan={6} className="admin-detail-cell">
                        {detailLoading ? <LoadingSpinner /> : userDetail ? (
                          <div className="admin-detail-panel">
                            <h4 className="admin-detail-heading">Account Status</h4>
                            <div className="admin-detail-grid">
                              <div className="admin-detail-field">
                                <span className="admin-detail-label">Email Confirmed</span>
                                <span className={`admin-status-badge ${userDetail.emailConfirmed ? 'admin-status-badge--ok' : 'admin-status-badge--warn'}`}>
                                  {userDetail.emailConfirmed ? 'Yes' : 'No'}
                                </span>
                              </div>
                              <div className="admin-detail-field">
                                <span className="admin-detail-label">2FA Enabled</span>
                                <span className={`admin-status-badge ${userDetail.has2FA ? 'admin-status-badge--ok' : 'admin-status-badge--warn'}`}>
                                  {userDetail.has2FA ? 'Yes' : 'No'}
                                </span>
                              </div>
                              <div className="admin-detail-field">
                                <span className="admin-detail-label">Locked Out</span>
                                <span className={`admin-status-badge ${userDetail.isLockedOut ? 'admin-status-badge--error' : 'admin-status-badge--ok'}`}>
                                  {userDetail.isLockedOut ? 'LOCKED' : 'No'}
                                </span>
                              </div>
                              <div className="admin-detail-field">
                                <span className="admin-detail-label">Failed Logins</span>
                                <span className={`admin-status-badge ${userDetail.accessFailedCount > 0 ? 'admin-status-badge--warn' : 'admin-status-badge--ok'}`}>
                                  {userDetail.accessFailedCount}
                                </span>
                              </div>
                              <div className="admin-detail-field">
                                <span className="admin-detail-label">Last Login</span>
                                <span className="admin-detail-value">
                                  {userDetail.lastLoginAt ? formatDateTime(userDetail.lastLoginAt) : 'Never'}
                                </span>
                              </div>
                              <div className="admin-detail-field">
                                <span className="admin-detail-label">Registered</span>
                                <span className="admin-detail-value">{formatDate(userDetail.createdAt)}</span>
                              </div>
                            </div>

                            {userDetail.memberships.length > 0 && (
                              <>
                                <h4 className="admin-detail-heading">Vault Memberships</h4>
                                {userDetail.memberships.map(m => (
                                  <div key={m.vaultId} className="admin-detail-membership">
                                    <strong>{m.vaultName}</strong>
                                    <span className="admin-detail-role">{roleLabel(m.role)}{m.isOwner ? ' (Owner)' : ''}</span>
                                    <span className="admin-detail-counts">
                                      {m.documentCount} docs, {m.noteCount} notes, {m.heirloomCount} heirlooms, {m.contactCount} contacts, {m.funeralPlanCount} plans
                                    </span>
                                  </div>
                                ))}
                              </>
                            )}

                            <AdminNotesEditor
                              notes={userDetail.adminNotes}
                              saveUrl={`/api/admin/users/${userDetail.userId}/notes`}
                              onSaved={notes => setUserDetail({ ...userDetail, adminNotes: notes })}
                            />

                            <AdminMessageComposer
                              userId={userDetail.userId}
                              userEmail={userDetail.email}
                            />

                            <h4 className="admin-detail-heading">Recent Activity</h4>
                            <ActivityFeed
                              items={userDetail.recentActivity}
                              showUser={false}
                              emptyMessage="No activity recorded for this user."
                            />

                            {actionMessage && (
                              <div className={`admin-action-message admin-action-message--${actionMessage.type}`}>
                                {actionMessage.text}
                              </div>
                            )}

                            <div className="admin-actions">
                              {!userDetail.emailConfirmed && (
                                <button className="admin-action-btn" onClick={() => handleConfirmEmail(u.userId, u.email)}>
                                  Confirm Email
                                </button>
                              )}
                              {userDetail.isLockedOut && (
                                <button className="admin-action-btn" onClick={() => handleUnlock(u.userId)}>
                                  Unlock Account
                                </button>
                              )}
                              {userDetail.has2FA && (
                                <button className="admin-action-btn" onClick={() => handleDisable2FA(u.userId)}>
                                  Disable 2FA
                                </button>
                              )}
                              <button className="admin-action-btn" onClick={() => handleSendPasswordReset(u.userId, u.email)}>
                                Send Password Reset
                              </button>
                              {u.email !== user?.email && (
                                <button className="admin-action-btn admin-action-btn--danger" onClick={() => handleDeleteUser(u)}>
                                  Delete User
                                </button>
                              )}
                              {u.email === user?.email && devFeaturesEnabled && (
                                <button className="admin-action-btn admin-action-btn--danger" onClick={handleResetMyAccount}>
                                  Reset My Account (E2E Test)
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="admin-empty">Failed to load user details.</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-subtle)' }}>No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VaultsTab() {
  const { user } = useAuth();
  const [vaults, setVaults] = useState<AdminVaultResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedVaultId, setExpandedVaultId] = useState<string | null>(null);
  const [vaultDetail, setVaultDetail] = useState<AdminVaultDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [storageQuotaInput, setStorageQuotaInput] = useState('');
  const [storageQuotaBusy, setStorageQuotaBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiGet<AdminVaultResponse[]>('/api/admin/vaults')
      .then(setVaults)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleExpand(vaultId: string) {
    if (expandedVaultId === vaultId) {
      setExpandedVaultId(null);
      setVaultDetail(null);
      return;
    }
    setExpandedVaultId(vaultId);
    setVaultDetail(null);
    setDetailLoading(true);
    try {
      const detail = await apiGet<AdminVaultDetailResponse>(`/api/admin/vaults/${vaultId}/detail`);
      setVaultDetail(detail);
    } catch {
      setVaultDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleStatusChange(e: React.ChangeEvent<HTMLSelectElement>, vaultId: string) {
    e.stopPropagation();
    const newStatus = Number(e.target.value) as VaultStatus;
    if (!window.confirm(`Change vault status to ${VaultStatus[newStatus]}?`)) {
      load();
      return;
    }
    try {
      await apiPut(`/api/admin/vaults/${vaultId}/status`, { status: newStatus });
      load();
    } catch {
      // ignore
    }
  }

  async function handleTierChange(e: React.ChangeEvent<HTMLSelectElement>, vaultId: string) {
    e.stopPropagation();
    const newTier = Number(e.target.value) as AccountTier;
    const tierName = AccountTier[newTier];
    if (!window.confirm(`Change vault tier to ${tierName}?`)) {
      load();
      return;
    }
    try {
      await apiPut(`/api/admin/vaults/${vaultId}/tier`, { accountTier: newTier });
      load();
    } catch {
      // ignore
    }
  }

  async function handleExtendTrial(vault: AdminVaultResponse, days: number) {
    if (!window.confirm(`Extend trial for "${vault.name}" by ${days} days?\n\nThis only changes the trial end date — no Stripe data is touched.`)) return;
    try {
      await apiPost(`/api/admin/vaults/${vault.id}/trial/extend`, { days });
      const detail = await apiGet<AdminVaultDetailResponse>(`/api/admin/vaults/${vault.id}/detail`);
      setVaultDetail(detail);
      load();
    } catch (err: any) {
      alert(err.message || 'Failed to extend trial.');
    }
  }

  async function handleSetStorageQuota(vaultId: string, quotaGb: number | null) {
    const label = quotaGb === null ? 'tier default' : `${quotaGb} GB`;
    if (!window.confirm(`Set video storage quota to ${label} for this vault?`)) return;
    setStorageQuotaBusy(true);
    try {
      await apiPut(`/api/admin/vaults/${vaultId}/storage-quota`, { quotaGb });
      const detail = await apiGet<AdminVaultDetailResponse>(`/api/admin/vaults/${vaultId}/detail`);
      setVaultDetail(detail);
      setStorageQuotaInput('');
    } catch {
      // ignore — user will see no update
    } finally {
      setStorageQuotaBusy(false);
    }
  }

  async function handleBillingReset(vault: AdminVaultResponse) {
    const msg = `Reset billing for "${vault.name}"?\n\nThis will clear all Stripe subscription data and return the vault to Trial tier (fresh 14-day trial). The owner will need to subscribe again.\n\nNote: cancel the subscription in the Stripe dashboard separately if needed.`;
    if (!window.confirm(msg)) return;
    try {
      await apiPost(`/api/admin/vaults/${vault.id}/billing/reset`, {});
      const detail = await apiGet<AdminVaultDetailResponse>(`/api/admin/vaults/${vault.id}/detail`);
      setVaultDetail(detail);
      load();
    } catch (err: any) {
      alert(err.message || 'Billing reset failed.');
    }
  }

  async function handleForceActivate(vault: AdminVaultResponse) {
    const subId = window.prompt(`Force-activate "${vault.name}" to Paid tier.\n\nPaste the Stripe subscription ID (sub_...) or leave blank to use the existing one:`);
    if (subId === null) return; // cancelled
    try {
      await apiPost(`/api/admin/vaults/${vault.id}/billing/force-activate`, { subscriptionId: subId || undefined });
      const detail = await apiGet<AdminVaultDetailResponse>(`/api/admin/vaults/${vault.id}/detail`);
      setVaultDetail(detail);
      load();
    } catch (err: any) {
      alert(err.message || 'Force-activate failed.');
    }
  }

  async function handleDelete(vault: AdminVaultResponse) {
    const msg = `DELETE vault "${vault.name}"?\n\nThis will permanently delete all documents, notes, heirlooms, and other data. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await apiDelete(`/api/admin/vaults/${vault.id}`);
      setExpandedVaultId(null);
      setVaultDetail(null);
      load();
    } catch (err: any) {
      alert(err.message || 'Delete failed.');
    }
  }

  const roleLabel = (role: VaultRole): string => {
    switch (role) {
      case VaultRole.Owner: return 'Owner';
      case VaultRole.Spouse: return 'Spouse';
      case VaultRole.Witness: return 'Witness';
      default: return '-';
    }
  };

  return (
    <div>
      {loading ? <LoadingSpinner /> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Tier</th>
                <th>Members</th>
                <th>Docs</th>
                <th>Notes</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {vaults.map(v => (
                <React.Fragment key={v.id}>
                  <tr
                    className={`admin-table-row-clickable${expandedVaultId === v.id ? ' admin-table-row-expanded' : ''}`}
                    onClick={() => toggleExpand(v.id)}
                  >
                    <td>{v.name}</td>
                    <td>{v.ownerName}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <select
                        className="admin-status-select"
                        value={v.status}
                        onChange={e => handleStatusChange(e, v.id)}
                      >
                        <option value={VaultStatus.Active}>Active</option>
                        <option value={VaultStatus.PastDue}>Past Due</option>
                        <option value={VaultStatus.ReadOnly}>Read Only</option>
                        <option value={VaultStatus.Suspended}>Suspended</option>
                      </select>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <select
                        className="admin-status-select"
                        value={v.accountTier ?? 0}
                        onChange={e => handleTierChange(e, v.id)}
                      >
                        <option value={0} disabled>Unknown</option>
                        <option value={AccountTier.Trial}>Trial</option>
                        <option value={AccountTier.Paid}>Paid</option>
                        <option value={AccountTier.ForeverFree}>Forever Free</option>
                      </select>
                    </td>
                    <td>{v.memberCount}</td>
                    <td>{v.documentCount}</td>
                    <td>{v.noteCount}</td>
                    <td>{formatDate(v.createdAt)}</td>
                  </tr>
                  {expandedVaultId === v.id && (
                    <tr>
                      <td colSpan={8} className="admin-detail-cell">
                        {detailLoading ? <LoadingSpinner /> : vaultDetail ? (
                          <div className="admin-detail-panel">
                            <h4 className="admin-detail-heading">Members</h4>
                            <div className="admin-vault-members">
                              {vaultDetail.members.map(m => (
                                <div key={m.userId} className="admin-vault-member">
                                  <span className="admin-vault-member-name">{m.fullName}</span>
                                  <span className="admin-vault-member-email">{m.email}</span>
                                  <span className="admin-detail-role">{roleLabel(m.role)}</span>
                                  <span className={`admin-status-badge ${m.has2FA ? 'admin-status-badge--ok' : 'admin-status-badge--warn'}`}>
                                    2FA: {m.has2FA ? 'Yes' : 'No'}
                                  </span>
                                  <span className="admin-detail-value">
                                    {m.lastLoginAt ? formatDateTime(m.lastLoginAt) : 'Never logged in'}
                                  </span>
                                </div>
                              ))}
                            </div>

                            <h4 className="admin-detail-heading">Content</h4>
                            <div className="admin-detail-counts-row">
                              <span>{vaultDetail.documentCount} docs</span>
                              <span>{vaultDetail.noteCount} notes</span>
                              <span>{vaultDetail.heirloomCount} heirlooms</span>
                              <span>{vaultDetail.contactCount} contacts</span>
                              <span>{vaultDetail.funeralPlanCount} plans</span>
                              <span>{vaultDetail.letterCount} letters</span>
                              <span>{vaultDetail.videoCount} videos</span>
                            </div>

                            <h4 className="admin-detail-heading">Billing</h4>
                            <div className="admin-billing-grid">
                              <div className="admin-billing-row">
                                <span className="admin-billing-label">Account Tier</span>
                                <span className={`admin-status-badge admin-tier-badge--${AccountTier[vaultDetail.accountTier].toLowerCase()}`}>
                                  {AccountTier[vaultDetail.accountTier]}
                                </span>
                              </div>
                              <div className="admin-billing-row">
                                <span className="admin-billing-label">Stripe Status</span>
                                <span className={`admin-status-badge ${
                                  vaultDetail.stripeSubscriptionStatus === 'active' ? 'admin-status-badge--ok' :
                                  vaultDetail.stripeSubscriptionStatus === 'past_due' ? 'admin-status-badge--warn' :
                                  vaultDetail.stripeSubscriptionStatus ? 'admin-status-badge--danger' :
                                  'admin-status-badge--muted'
                                }`}>
                                  {vaultDetail.stripeSubscriptionStatus ?? 'none'}
                                </span>
                              </div>
                              {vaultDetail.trialEndsAt && (
                                <div className="admin-billing-row">
                                  <span className="admin-billing-label">Trial Ends</span>
                                  <span className="admin-billing-value">{formatDate(vaultDetail.trialEndsAt)}</span>
                                </div>
                              )}
                              {vaultDetail.scheduledDeletionAt && (
                                <div className="admin-billing-row">
                                  <span className="admin-billing-label">Scheduled Deletion</span>
                                  <span className="admin-billing-value admin-billing-value--danger">{formatDate(vaultDetail.scheduledDeletionAt)}</span>
                                </div>
                              )}
                              <div className="admin-billing-row">
                                <span className="admin-billing-label">Stripe Customer</span>
                                {vaultDetail.stripeCustomerId
                                  ? <span className="admin-billing-mono">
                                      {vaultDetail.stripeCustomerId}&nbsp;
                                      <a href={`https://dashboard.stripe.com/customers/${vaultDetail.stripeCustomerId}`} target="_blank" rel="noreferrer" className="admin-stripe-link">Open in Stripe →</a>
                                    </span>
                                  : <span className="admin-billing-mono">—</span>}
                              </div>
                              <div className="admin-billing-row">
                                <span className="admin-billing-label">Stripe Subscription</span>
                                {vaultDetail.stripeSubscriptionId
                                  ? <span className="admin-billing-mono">
                                      {vaultDetail.stripeSubscriptionId}&nbsp;
                                      <a href={`https://dashboard.stripe.com/subscriptions/${vaultDetail.stripeSubscriptionId}`} target="_blank" rel="noreferrer" className="admin-stripe-link">Open in Stripe →</a>
                                    </span>
                                  : <span className="admin-billing-mono">—</span>}
                              </div>
                            </div>
                            <div className="admin-actions" style={{ marginTop: 12 }}>
                              {v.accountTier === AccountTier.Trial && (
                                <>
                                  <button
                                    className="admin-action-btn admin-action-btn--ok"
                                    onClick={() => handleExtendTrial(v, 7)}
                                  >
                                    Extend Trial +7 Days
                                  </button>
                                  <button
                                    className="admin-action-btn admin-action-btn--ok"
                                    onClick={() => handleExtendTrial(v, 14)}
                                  >
                                    Extend Trial +14 Days
                                  </button>
                                </>
                              )}
                              <button
                                className="admin-action-btn admin-action-btn--ok"
                                onClick={() => handleForceActivate(v)}
                              >
                                Manually Promote to Paid (webhook failed — payment confirmed in Stripe)
                              </button>
                              <button
                                className="admin-action-btn admin-action-btn--danger"
                                onClick={() => handleBillingReset(v)}
                              >
                                Move to Free Trial (clears Stripe data — use to let ForeverFree user subscribe voluntarily)
                              </button>
                            </div>

                            <h4 className="admin-detail-heading">Video Storage Quota</h4>
                            <div className="admin-billing-grid">
                              <div className="admin-billing-row">
                                <span className="admin-billing-label">Current Quota</span>
                                <span className="admin-billing-value">
                                  {vaultDetail.videoStorageQuotaOverrideBytes != null
                                    ? `${(vaultDetail.videoStorageQuotaOverrideBytes / (1024 ** 3)).toFixed(1)} GB (custom override)`
                                    : 'Tier default (5 GB Paid/FF · 512 MB Trial)'}
                                </span>
                              </div>
                            </div>
                            <div className="admin-actions" style={{ marginTop: 8, alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                              <input
                                type="number"
                                min="1"
                                max="2048"
                                step="1"
                                placeholder="GB (e.g. 20)"
                                value={storageQuotaInput}
                                onChange={e => setStorageQuotaInput(e.target.value)}
                                className="admin-quota-input"
                                disabled={storageQuotaBusy}
                              />
                              <button
                                className="admin-action-btn admin-action-btn--ok"
                                disabled={storageQuotaBusy || !storageQuotaInput}
                                onClick={() => handleSetStorageQuota(vaultDetail.id, parseFloat(storageQuotaInput))}
                              >
                                {storageQuotaBusy ? 'Saving…' : 'Set Custom Quota'}
                              </button>
                              {vaultDetail.videoStorageQuotaOverrideBytes != null && (
                                <button
                                  className="admin-action-btn admin-action-btn--danger"
                                  disabled={storageQuotaBusy}
                                  onClick={() => handleSetStorageQuota(vaultDetail.id, null)}
                                >
                                  Clear Override (restore tier default)
                                </button>
                              )}
                            </div>

                            <AdminNotesEditor
                              notes={vaultDetail.adminNotes}
                              saveUrl={`/api/admin/vaults/${vaultDetail.id}/notes`}
                              onSaved={notes => setVaultDetail({ ...vaultDetail, adminNotes: notes })}
                            />

                            <AdminMessageComposer userId={vaultDetail.ownerId} userEmail={vaultDetail.ownerEmail} />

                            <h4 className="admin-detail-heading">Recent Activity</h4>
                            <ActivityFeed
                              items={vaultDetail.recentActivity}
                              showVault={false}
                              emptyMessage="No activity recorded for this vault."
                            />

                            <div className="admin-actions">
                              {v.ownerEmail !== user?.email && (
                                <button className="admin-action-btn admin-action-btn--danger" onClick={() => handleDelete(v)}>
                                  Delete Vault
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="admin-empty">Failed to load vault details.</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {vaults.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-subtle)' }}>No vaults found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InvitationsTab() {
  const [invitations, setInvitations] = useState<AdminInvitationResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiGet<AdminInvitationResponse[]>('/api/admin/invitations')
      .then(setInvitations)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleResend(inv: AdminInvitationResponse) {
    if (!window.confirm(`Resend invitation to "${inv.email}" for vault "${inv.vaultName}"?\n\nThis resets the token and extends expiry by 14 days.`)) return;
    try {
      await apiPost(`/api/admin/invitations/${inv.id}/resend`, {});
      load();
    } catch (err: any) {
      alert(err.message || 'Resend failed.');
    }
  }

  async function handleDelete(inv: AdminInvitationResponse) {
    if (!window.confirm(`Delete invitation for "${inv.email}" to "${inv.vaultName}"?`)) return;
    try {
      await apiDelete(`/api/admin/invitations/${inv.id}`);
      load();
    } catch {
      // ignore
    }
  }

  function statusLabel(inv: AdminInvitationResponse): { text: string; className: string } {
    if (inv.isAccepted) return { text: 'Accepted', className: 'admin-invite-accepted' };
    if (inv.isExpired) return { text: 'Expired', className: 'admin-invite-expired' };
    return { text: 'Pending', className: 'admin-invite-pending' };
  }

  return (
    <div>
      {loading ? <LoadingSpinner /> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Vault</th>
                <th>Invited By</th>
                <th>Sent</th>
                <th>Expires</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invitations.map(inv => {
                const status = statusLabel(inv);
                return (
                  <tr key={inv.id}>
                    <td>{inv.email}</td>
                    <td>{inv.role}</td>
                    <td>{inv.vaultName}</td>
                    <td>{inv.inviterName}</td>
                    <td>{formatDate(inv.invitedAt)}</td>
                    <td>{formatDate(inv.expiresAt)}</td>
                    <td>
                      <span className={`admin-invite-status ${status.className}`}>
                        {status.text}
                      </span>
                    </td>
                    <td style={{ display: 'flex', gap: '6px' }}>
                      {!inv.isAccepted && (
                        <>
                          <button className="admin-action-btn" style={{ fontSize: '0.75rem', padding: '4px 10px' }} onClick={() => handleResend(inv)}>
                            Resend
                          </button>
                          <button className="admin-delete-btn" onClick={() => handleDelete(inv)}>
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {invitations.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-subtle)' }}>No invitations found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ForeverFreeTab() {
  const [invites, setInvites] = useState<ForeverFreeInvitationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    apiGet<ForeverFreeInvitationResponse[]>('/api/admin/forever-free/invites')
      .then(setInvites)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSend() {
    if (!email.trim()) return;
    setSending(true);
    setMessage(null);
    try {
      await apiPost('/api/admin/forever-free/invite', { email: email.trim(), personalMessage: personalMessage.trim() || null });
      setMessage({ text: `Invitation sent to ${email.trim()}.`, type: 'success' });
      setEmail('');
      setPersonalMessage('');
      load();
    } catch (err: any) {
      setMessage({ text: err.message || 'Failed to send invitation.', type: 'error' });
    } finally {
      setSending(false);
    }
  }

  async function handleResend(inv: ForeverFreeInvitationResponse) {
    if (!window.confirm(`Resend Forever Free invitation to "${inv.email}"?`)) return;
    try {
      await apiPost(`/api/admin/forever-free/invites/${inv.id}/resend`, {});
      setMessage({ text: `Invitation resent to ${inv.email}.`, type: 'success' });
      load();
    } catch (err: any) {
      setMessage({ text: err.message || 'Resend failed.', type: 'error' });
    }
  }

  async function handleCancel(inv: ForeverFreeInvitationResponse) {
    if (!window.confirm(`Cancel the Forever Free invitation for "${inv.email}"?`)) return;
    try {
      await apiDelete(`/api/admin/forever-free/invites/${inv.id}`);
      setMessage({ text: `Invitation for ${inv.email} cancelled.`, type: 'success' });
      load();
    } catch (err: any) {
      setMessage({ text: err.message || 'Cancel failed.', type: 'error' });
    }
  }

  const accepted = invites.filter(i => i.isAccepted);
  const pending = invites.filter(i => !i.isAccepted && !i.isExpired);
  const expired = invites.filter(i => !i.isAccepted && i.isExpired);

  return (
    <div>
      {/* Send form */}
      <div className="admin-ff-send-panel" style={{ marginBottom: 20 }}>
        <h3 className="admin-detail-heading">Send Forever Free Invitation</h3>
        <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginBottom: 16 }}>
          Sends a branded gift invitation with lifetime access. Recipient must not already own a vault.
        </p>
        <div className="admin-ff-form-row">
          <div className="admin-ff-input-wrap">
            <label className="admin-ff-label" htmlFor="ff-email-input">Recipient email address</label>
            <input
              id="ff-email-input"
              type="email"
              className="admin-ff-input"
              placeholder="friend@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="off"
            />
          </div>
          <button
            type="button"
            className="admin-ff-send-btn"
            disabled={sending || !email.trim()}
            onClick={handleSend}
          >
            {sending ? 'Sending…' : 'Send Invitation'}
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          <label className="admin-ff-label" htmlFor="ff-message-input">Personal message <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional — appears in the invitation email)</span></label>
          <textarea
            id="ff-message-input"
            className="admin-ff-input"
            placeholder="e.g. Rick, I built this for families like ours — would love your honest feedback."
            value={personalMessage}
            onChange={e => setPersonalMessage(e.target.value)}
            rows={3}
            maxLength={500}
            style={{ resize: 'vertical', marginTop: 4 }}
          />
          {personalMessage.length > 0 && (
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2, textAlign: 'right' }}>
              {personalMessage.length}/500
            </p>
          )}
        </div>
        {!email.trim() && (
          <p className="admin-ff-hint">Enter an email address above to enable the Send button.</p>
        )}
        {message && (
          <div className={`admin-action-message admin-action-message--${message.type}`} style={{ marginTop: 12 }}>
            {message.text}
          </div>
        )}
      </div>

      {/* Summary counts */}
      <div className="admin-stats-compact" style={{ marginBottom: 16 }}>
        <div className="admin-stats-compact-group">
          <span className="admin-stats-compact-heading">Forever Free Invitations</span>
          <span className="admin-stats-compact-item">
            <span className="admin-stats-compact-value">{accepted.length}</span>{' '}
            <span className="admin-stats-compact-label">accepted</span>
          </span>
          <span className="admin-stats-compact-item">
            <span className="admin-stats-compact-value">{pending.length}</span>{' '}
            <span className="admin-stats-compact-label">pending</span>
          </span>
          <span className="admin-stats-compact-item">
            <span className="admin-stats-compact-value">{expired.length}</span>{' '}
            <span className="admin-stats-compact-label">expired</span>
          </span>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Sent By</th>
                <th>Sent</th>
                <th>Expires</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invites.map(inv => (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td>{inv.sentByAdminName}</td>
                  <td>{formatDate(inv.sentAt)}</td>
                  <td>{formatDate(inv.expiresAt)}</td>
                  <td>
                    <span className={`admin-invite-status ${
                      inv.isAccepted ? 'admin-invite-accepted'
                      : inv.isExpired ? 'admin-invite-expired'
                      : 'admin-invite-pending'
                    }`}>
                      {inv.isAccepted ? 'Accepted' : inv.isExpired ? 'Expired' : 'Pending'}
                    </span>
                  </td>
                  <td>
                    {!inv.isAccepted && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="admin-action-btn" style={{ fontSize: '0.75rem', padding: '4px 10px' }} onClick={() => handleResend(inv)}>
                          Resend
                        </button>
                        <button className="admin-delete-btn" onClick={() => handleCancel(inv)}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {invites.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-subtle)' }}>
                    No Forever Free invitations yet. Send the first one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActivityTab() {
  const [activity, setActivity] = useState<AdminActivityResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [vaultName, setVaultName] = useState('');
  const [count, setCount] = useState(50);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ count: String(count) });
    if (userEmail.trim()) params.set('userEmail', userEmail.trim());
    if (vaultName.trim()) params.set('vaultName', vaultName.trim());
    try {
      const data = await apiGet<AdminActivityResponse[]>(`/api/admin/activity?${params}`);
      setActivity(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [userEmail, vaultName, count]);

  useEffect(() => { load(); }, []);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    load();
  }

  function handleClear() {
    setUserEmail('');
    setVaultName('');
    setCount(50);
    // re-fetch with cleared values directly
    setLoading(true);
    apiGet<AdminActivityResponse[]>('/api/admin/activity?count=50')
      .then(setActivity)
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  const isFiltered = userEmail.trim() || vaultName.trim() || count !== 50;

  return (
    <div className="admin-activity-tab">
      <form className="admin-activity-filters" onSubmit={handleSearch}>
        <input
          className="admin-filter-input"
          placeholder="Filter by user email"
          value={userEmail}
          onChange={e => setUserEmail(e.target.value)}
        />
        <input
          className="admin-filter-input"
          placeholder="Filter by vault name"
          value={vaultName}
          onChange={e => setVaultName(e.target.value)}
        />
        <select
          className="admin-filter-select"
          value={count}
          onChange={e => setCount(Number(e.target.value))}
        >
          <option value={25}>25 results</option>
          <option value={50}>50 results</option>
          <option value={100}>100 results</option>
          <option value={250}>250 results</option>
        </select>
        <button type="submit" className="admin-action-btn">Search</button>
        {isFiltered && (
          <button type="button" className="btn-link" onClick={handleClear}>Clear</button>
        )}
      </form>

      {loading
        ? <LoadingSpinner />
        : activity.length === 0
          ? <div className="admin-empty">No activity found{isFiltered ? ' matching these filters' : ''}.</div>
          : <ul className="admin-activity-list">
              {activity.map(a => (
                <li key={a.id} className="admin-activity-item">
                  <span className="admin-activity-time">{formatDateTime(a.timestamp)}</span>
                  <span className="admin-activity-detail">
                    <span className="admin-activity-user">{a.userEmail ?? 'System'}</span>
                    {' '}
                    <span className="admin-activity-action">{a.action}</span>
                    {' '}
                    {a.entityType}
                    {a.vaultName && (
                      <span className="admin-activity-vault"> in {a.vaultName}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
      }
    </div>
  );
}

function actionClass(action: string): string {
  const a = action.toLowerCase();
  if (a === 'created') return 'created';
  if (a === 'updated') return 'updated';
  if (a === 'deleted') return 'deleted';
  if (a.includes('login') || a.includes('logout') || a.includes('2fa') || a.includes('password')) return 'auth';
  return 'default';
}

function ActivityFeed({
  items,
  showUser = true,
  showVault = true,
  emptyMessage = 'No activity recorded.',
}: {
  items: AdminActivityResponse[];
  showUser?: boolean;
  showVault?: boolean;
  emptyMessage?: string;
}) {
  if (items.length === 0) return <div className="admin-empty">{emptyMessage}</div>;
  return (
    <ul className="admin-activity-list">
      {items.map(a => (
        <li key={a.id} className="admin-activity-item admin-activity-item--feed">
          <span className="admin-activity-time">{formatDateTime(a.timestamp)}</span>
          <span className={`admin-activity-badge admin-activity-badge--${actionClass(a.action)}`}>
            {a.action}
          </span>
          <span className="admin-activity-detail">
            <span className="admin-activity-entity">{a.entityType}</span>
            {showVault && a.vaultName && (
              <span className="admin-activity-vault"> · {a.vaultName}</span>
            )}
            {showUser && a.userEmail && (
              <span className="admin-activity-by"> by {a.userEmail}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── Health Tab (merged from former Health + Settings tabs) ───────────────────

interface StripeHealthResult {
  check: string;
  ok: boolean;
  detail: string;
}

interface StripeHealthResponse {
  allOk: boolean;
  results: StripeHealthResult[];
}

function HealthTab() {
  // Azure + job last-run status
  const [data, setData] = useState<AdminHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Stripe config health (on-demand)
  const [stripeResult, setStripeResult] = useState<StripeHealthResponse | null>(null);
  const [stripeChecking, setStripeChecking] = useState(false);

  // Job triggers
  const [maintenanceRunning, setMaintenanceRunning] = useState(false);
  const [maintenanceResult, setMaintenanceResult] = useState<string | null>(null);
  const [reminderRunning, setReminderRunning] = useState(false);
  const [reminderResult, setReminderResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiGet<AdminHealthResponse>('/api/admin/health');
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load health status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function runStripeHealthCheck() {
    setStripeChecking(true);
    setStripeResult(null);
    try {
      const result = await apiGet<StripeHealthResponse>('/api/admin/stripe/health');
      setStripeResult(result);
    } catch (err: any) {
      alert(err.message || 'Stripe health check failed.');
    } finally {
      setStripeChecking(false);
    }
  }

  async function runMaintenanceNow() {
    if (!window.confirm('Run vault maintenance now?\n\nThis operates on real vault data (flipping expired trials, sending warning emails, purging overdue vaults). Only proceed if you understand the impact.')) return;
    setMaintenanceRunning(true);
    setMaintenanceResult(null);
    try {
      const result = await apiPost<{ message: string }>('/api/admin/maintenance/run', {});
      setMaintenanceResult(result.message);
      await load(); // refresh last-run times after job completes
    } catch (err: any) {
      setMaintenanceResult(`Error: ${err.message || 'Unknown error'}`);
    } finally {
      setMaintenanceRunning(false);
    }
  }

  async function runTrialRemindersNow() {
    if (!window.confirm('Run trial reminder emails now?\n\nThis sends real emails to trial vault owners at the 30/21/14 day milestones. Only proceed if you understand the impact.')) return;
    setReminderRunning(true);
    setReminderResult(null);
    try {
      const result = await apiPost<{ message: string }>('/api/admin/trial-reminders/run', {});
      setReminderResult(result.message);
      await load(); // refresh last-run times after job completes
    } catch (err: any) {
      setReminderResult(`Error: ${err.message || 'Unknown error'}`);
    } finally {
      setReminderRunning(false);
    }
  }

  function fmtRunTime(iso: string | null): string {
    if (!iso) return 'Not run since last restart';
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  }

  function fmtUtc(iso: string | null): string {
    if (!iso) return '';
    return new Date(iso).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
  }

  return (
    <div className="admin-settings">
      <h2 className="admin-section-title">System Health</h2>

      {error && <div className="admin-empty" style={{ color: 'var(--error)', marginBottom: 16 }}>{error}</div>}

      {/* ── Service Dependencies ── */}
      <div className="admin-settings-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 className="admin-detail-heading" style={{ margin: 0 }}>Service Dependencies</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="admin-action-btn admin-action-btn--ok" onClick={load} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh Azure'}
            </button>
            <button className="admin-action-btn admin-action-btn--ok" onClick={runStripeHealthCheck} disabled={stripeChecking}>
              {stripeChecking ? 'Checking…' : 'Check Stripe Config'}
            </button>
          </div>
        </div>
        <p className="admin-settings-desc">
          Azure services auto-check on page load. Stripe verifies your keys, price IDs, and webhook secret — run after rotating any credentials.
        </p>

        {loading ? <LoadingSpinner /> : data && (
          <div className="admin-health-results">
            <div className={`admin-health-summary ${(data.blobStorage.ok && data.emailService.ok) ? 'admin-health-summary--ok' : 'admin-health-summary--fail'}`}>
              {(data.blobStorage.ok && data.emailService.ok) ? '✓ Azure services reachable' : '✗ One or more Azure services unreachable'}
            </div>
            <div className={`admin-health-row ${data.blobStorage.ok ? 'admin-health-row--ok' : 'admin-health-row--fail'}`}>
              <span className="admin-health-check">Blob Storage</span>
              <span className={`admin-status-badge ${data.blobStorage.ok ? 'admin-status-badge--ok' : 'admin-status-badge--danger'}`}>
                {data.blobStorage.ok ? 'OK' : 'FAIL'}
              </span>
              <span className="admin-health-detail">
                {data.blobStorage.detail}
                {data.blobStorage.latencyMs != null && data.blobStorage.latencyMs > 0 && (
                  <span style={{ color: 'var(--text-subtle)', marginLeft: 8 }}>{data.blobStorage.latencyMs}ms</span>
                )}
              </span>
            </div>
            <div className={`admin-health-row ${data.emailService.ok ? 'admin-health-row--ok' : 'admin-health-row--fail'}`}>
              <span className="admin-health-check">ACS Email</span>
              <span className={`admin-status-badge ${data.emailService.ok ? 'admin-status-badge--ok' : 'admin-status-badge--danger'}`}>
                {data.emailService.ok ? 'OK' : 'FAIL'}
              </span>
              <span className="admin-health-detail">{data.emailService.detail}</span>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-subtle)', marginTop: 8 }}>
              Checked at {fmtUtc(data.checkedAt)}
            </p>
          </div>
        )}

        {stripeResult && (
          <div className="admin-health-results" style={{ marginTop: 12 }}>
            <div className={`admin-health-summary ${stripeResult.allOk ? 'admin-health-summary--ok' : 'admin-health-summary--fail'}`}>
              {stripeResult.allOk ? '✓ Stripe config valid' : '✗ One or more Stripe checks failed'}
            </div>
            {stripeResult.results.map(r => (
              <div key={r.check} className={`admin-health-row ${r.ok ? 'admin-health-row--ok' : 'admin-health-row--fail'}`}>
                <span className="admin-health-check">{r.check}</span>
                <span className={`admin-status-badge ${r.ok ? 'admin-status-badge--ok' : 'admin-status-badge--danger'}`}>
                  {r.ok ? 'PASS' : 'FAIL'}
                </span>
                <span className="admin-health-detail">{r.detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Background Jobs ── */}
      <div className="admin-settings-card">
        <h3 className="admin-detail-heading">Background Jobs</h3>
        <p className="admin-settings-desc">
          Last run times reset on service restart. "Run Now" triggers the job immediately — useful for responding to a customer issue without waiting for the next scheduled run. Both operate on real users and vaults.
        </p>

        {loading ? <LoadingSpinner /> : (
          <>
            <div className="admin-job-row">
              <div className="admin-job-info">
                <strong>Vault Maintenance</strong>
                <span className="admin-job-schedule">Runs every 6 hours automatically · 7 steps</span>
                <span className="admin-job-desc">
                  Flips expired trials to read-only, schedules deletions, sends pre-expiry reminders (day 11 &amp; 13), retention warnings, purges overdue vaults, cleans orphaned video uploads, and sends video quota warnings.
                </span>
                {data && (
                  <span className="admin-job-desc">
                    Last run: <strong>{fmtRunTime(data.lastMaintenanceRun)}</strong>
                    {data.lastMaintenanceRun && <span style={{ color: 'var(--text-subtle)', marginLeft: 8 }}>({fmtUtc(data.lastMaintenanceRun)})</span>}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <span className={`admin-status-badge ${data?.lastMaintenanceRun ? 'admin-status-badge--ok' : 'admin-status-badge--warn'}`}>
                  {data?.lastMaintenanceRun ? 'RAN' : 'PENDING'}
                </span>
                <button
                  className="admin-action-btn admin-action-btn--warn"
                  onClick={runMaintenanceNow}
                  disabled={maintenanceRunning}
                >
                  {maintenanceRunning ? 'Running…' : 'Run Now'}
                </button>
              </div>
            </div>

            {/* Step breakdown — always visible; shows PENDING placeholder before first run */}
            {data && (() => {
              const STEP_LABELS: { step: string; description: string }[] = [
                { step: 'FlipExpiredTrials',      description: 'Expired trials → ReadOnly' },
                { step: 'ScheduleLapsedDeletion', description: 'Lapsed Paid → deletion scheduled' },
                { step: 'SendTrialExpiryReminders', description: 'Trial expiry reminders sent' },
                { step: 'SendRetentionWarnings',  description: 'Retention warning emails sent' },
                { step: 'PurgeExpiredVaults',     description: 'Expired vaults hard-deleted' },
                { step: 'CleanOrphanedVideos',    description: 'Orphaned video uploads cleaned' },
                { step: 'SendVideoQuotaWarnings', description: 'Video quota warnings sent' },
              ];
              const stepMap = new Map(data.lastMaintenanceSteps.map((s: AdminMaintenanceStepResult) => [s.step, s]));
              return (
                <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                        <th style={{ padding: '4px 8px 8px 0', fontWeight: 600, letterSpacing: '0.03em' }}>Step</th>
                        <th style={{ padding: '4px 8px 8px', fontWeight: 600, textAlign: 'center', letterSpacing: '0.03em' }}>Count</th>
                        <th style={{ padding: '4px 0 8px 8px', fontWeight: 600, textAlign: 'right', letterSpacing: '0.03em' }}>Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {STEP_LABELS.map(({ step, description }) => {
                        const result = stepMap.get(step);
                        return (
                          <tr key={step} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '6px 8px 6px 0', color: 'var(--text-body)' }}>{description}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              {result ? (
                                <span style={{ fontWeight: 700, color: result.count > 0 ? 'var(--gold)' : 'var(--text-subtle)' }}>
                                  {result.count}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--text-subtle)', fontStyle: 'italic' }}>–</span>
                              )}
                            </td>
                            <td style={{ padding: '6px 0 6px 8px', textAlign: 'right', color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>
                              {result ? fmtRunTime(result.recordedAt) : 'not run yet'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {maintenanceResult && (
              <div className={`admin-job-result ${maintenanceResult.startsWith('Error') ? 'admin-job-result--error' : 'admin-job-result--ok'}`}>
                {maintenanceResult}
              </div>
            )}

            <div className="admin-job-row" style={{ marginTop: 16 }}>
              <div className="admin-job-info">
                <strong>Trial Reminder Emails</strong>
                <span className="admin-job-schedule">Runs daily at 8am UTC automatically · 5 steps</span>
                <span className="admin-job-desc">
                  Sends reminder emails at 30, 21, and 14 days before trial expiry (extended trials). Standard day-11 and day-13 reminders run within Vault Maintenance. Admin-owned vaults are skipped.
                </span>
                {data && (
                  <span className="admin-job-desc">
                    Last run: <strong>{fmtRunTime(data.lastTrialReminderRun)}</strong>
                    {data.lastTrialReminderRun && <span style={{ color: 'var(--text-subtle)', marginLeft: 8 }}>({fmtUtc(data.lastTrialReminderRun)})</span>}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <span className={`admin-status-badge ${data?.lastTrialReminderRun ? 'admin-status-badge--ok' : 'admin-status-badge--warn'}`}>
                  {data?.lastTrialReminderRun ? 'RAN' : 'PENDING'}
                </span>
                <button
                  className="admin-action-btn admin-action-btn--warn"
                  onClick={runTrialRemindersNow}
                  disabled={reminderRunning}
                >
                  {reminderRunning ? 'Running…' : 'Run Now'}
                </button>
              </div>
            </div>

            {/* Trial reminder step breakdown — always visible */}
            {data && (() => {
              const TRIAL_STEP_LABELS: { step: string; description: string }[] = [
                { step: 'VaultsInWindow', description: 'Trial vaults in 30-day window' },
                { step: 'Sent30Day',      description: '30-day reminders sent' },
                { step: 'Sent21Day',      description: '21-day reminders sent' },
                { step: 'Sent14Day',      description: '14-day reminders sent' },
                { step: 'AdminSkipped',   description: 'Admin vaults skipped' },
              ];
              const stepMap = new Map((data.lastTrialReminderSteps ?? []).map((s: AdminMaintenanceStepResult) => [s.step, s]));
              return (
                <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                        <th style={{ padding: '4px 8px 8px 0', fontWeight: 600, letterSpacing: '0.03em' }}>Step</th>
                        <th style={{ padding: '4px 8px 8px', fontWeight: 600, textAlign: 'center', letterSpacing: '0.03em' }}>Count</th>
                        <th style={{ padding: '4px 0 8px 8px', fontWeight: 600, textAlign: 'right', letterSpacing: '0.03em' }}>Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {TRIAL_STEP_LABELS.map(({ step, description }) => {
                        const result = stepMap.get(step);
                        return (
                          <tr key={step} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '6px 8px 6px 0', color: 'var(--text-body)' }}>{description}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              {result ? (
                                <span style={{ fontWeight: 700, color: result.count > 0 ? 'var(--gold)' : 'var(--text-subtle)' }}>
                                  {result.count}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--text-subtle)', fontStyle: 'italic' }}>–</span>
                              )}
                            </td>
                            <td style={{ padding: '6px 0 6px 8px', textAlign: 'right', color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>
                              {result ? fmtRunTime(result.recordedAt) : 'not run yet'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {reminderResult && (
              <div className={`admin-job-result ${reminderResult.startsWith('Error') ? 'admin-job-result--error' : 'admin-job-result--ok'}`}>
                {reminderResult}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const EMAIL_TEMPLATE_LABELS: Record<string, string> = {
  'email-confirmation':      'Email Confirmation',
  'password-reset':          'Password Reset',
  '2fa-code':                '2FA Verification Code',
  'vault-invitation':        'Vault Invitation',
  'forever-free-invite':     'Forever Free Invite',
  'trial-expiry-reminder':        'Trial Expiry Reminder',
  'trial-expiry-reminder-empty':  'Trial Expiry Reminder (Empty Vault)',
  'retention-warning':       'Retention Warning',
  'video-quota-warning':     'Video Storage Quota Warning',
  'chat-notification':       'Chat Message Notification',
  'letter-vote':             'Letter Vote Notification',
  'advisor-revoked':         'Advisor Revoked',
  'member-left':             'Member Left Vault',
  'vault-closure-confirmation': 'Vault Closure Confirmation (Owner)',
  'vault-closed-member':     'Vault Closed (Member Notification)',
  'forever-free-conflict':   'Forever Free Conflict (Admin)',
  'admin-message':           'Admin Message to User',
};

function EmailsTab() {
  const [templates, setTemplates] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [testAddress, setTestAddress] = useState('support@cedarvaultapp.com');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [showWelcomePreview, setShowWelcomePreview] = useState(false);

  useEffect(() => {
    apiGet<string[]>('/api/admin/email/templates')
      .then(t => {
        setTemplates(t);
        if (t.length > 0) setSelected(t[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingPreview(true);
    setPreviewHtml('');
    apiGetText(`/api/admin/email/preview/${selected}`)
      .then(html => setPreviewHtml(html))
      .catch(() => setPreviewHtml('<p style="color:red;padding:16px;">Failed to load preview.</p>'))
      .finally(() => setLoadingPreview(false));
  }, [selected]);

  async function sendTest() {
    if (!selected) return;
    setSending(true);
    setSendResult(null);
    try {
      const result = await apiPost<{ message: string }>('/api/admin/email/send-test', {
        template: selected,
        toAddress: testAddress,
      });
      setSendResult({ ok: true, message: result.message });
    } catch (err: any) {
      setSendResult({ ok: false, message: err.message || 'Send failed.' });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="admin-emails">
      <h2 className="admin-section-title">Email Templates</h2>
      <p className="admin-settings-desc">
        Preview every transactional email template with representative dummy data.
        Use "Send Test" to fire a real email through ACS to verify delivery and rendering in an actual inbox.
      </p>

      <div className="admin-email-toolbar">
        <div className="admin-email-selector">
          <label htmlFor="email-template-select">Template</label>
          <select
            id="email-template-select"
            value={selected}
            onChange={e => { setSelected(e.target.value); setSendResult(null); }}
          >
            {templates.map(t => (
              <option key={t} value={t}>{EMAIL_TEMPLATE_LABELS[t] ?? t}</option>
            ))}
          </select>
        </div>

        <div className="admin-email-send-test">
          <input
            type="email"
            value={testAddress}
            onChange={e => setTestAddress(e.target.value)}
            placeholder="test@example.com"
            className="admin-email-address-input"
          />
          <button
            className="admin-action-btn admin-action-btn--ok"
            onClick={sendTest}
            disabled={sending || !selected}
          >
            {sending ? 'Sending…' : 'Send Test'}
          </button>
        </div>
      </div>

      {sendResult && (
        <div className={`admin-job-result ${sendResult.ok ? 'admin-job-result--ok' : 'admin-job-result--error'}`} style={{ marginBottom: '12px' }}>
          {sendResult.message}
        </div>
      )}

      <div className="admin-email-preview-frame">
        {loadingPreview && <div className="admin-email-preview-loading">Loading preview…</div>}
        {!loadingPreview && previewHtml && (
          <iframe
            srcDoc={previewHtml}
            title="Email preview"
            className="admin-email-iframe"
            sandbox="allow-same-origin"
          />
        )}
      </div>

      <div className="admin-section-divider" />
      <h3 className="admin-section-subtitle">UI Previews</h3>
      <p className="admin-settings-desc">
        Preview in-app UI content. These render exactly as a user would see them — no data is saved or changed.
      </p>
      <div className="admin-ui-previews">
        <div className="admin-ui-preview-row">
          <div>
            <strong>Welcome Modal</strong>
            <p className="admin-ui-preview-desc">Shown once to new vault owners on first entry. Introduces the vault and points to Estate Readiness.</p>
          </div>
          <button className="admin-action-btn admin-action-btn--ok" onClick={() => setShowWelcomePreview(true)}>
            Preview
          </button>
        </div>
      </div>

      {showWelcomePreview && (
        <WelcomeModal isPreview onDismiss={() => setShowWelcomePreview(false)} />
      )}
    </div>
  );
}

// ── Admin Notes Editor ───────────────────────────────────────────────────────

function AdminNotesEditor({ notes, saveUrl, onSaved }: {
  notes: string | null;
  saveUrl: string;
  onSaved: (notes: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const req: SaveAdminNotesRequest = { notes: draft.trim() || null };
      await apiPut(saveUrl, req);
      onSaved(draft.trim() || null);
      setEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save notes.');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setDraft(notes ?? '');
    setEditing(false);
    setError('');
  }

  return (
    <div className="admin-notes-section">
      <div className="admin-notes-header">
        <h4 className="admin-detail-heading" style={{ margin: 0 }}>Admin Notes</h4>
        {!editing && (
          <button className="btn-link" onClick={() => { setDraft(notes ?? ''); setEditing(true); }}>
            {notes ? 'Edit' : '+ Add note'}
          </button>
        )}
      </div>

      {editing ? (
        <div className="admin-notes-editor">
          <textarea
            className="admin-notes-textarea"
            rows={4}
            placeholder="e.g. Extended trial 2x — user was travelling. Converted from FF at their request."
            value={draft}
            onChange={e => setDraft(e.target.value)}
            autoFocus
          />
          {error && <div className="admin-error-msg" style={{ marginTop: 6 }}>{error}</div>}
          <div className="admin-notes-actions">
            <button className="admin-action-btn admin-action-btn--ok" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn-link" onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      ) : (
        <p className="admin-notes-display">
          {notes ?? <span className="admin-notes-empty">No notes yet.</span>}
        </p>
      )}
    </div>
  );
}

// ── Admin Message Composer ────────────────────────────────────────────────────

function AdminMessageComposer({ userId, userEmail }: { userId: string; userEmail: string }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  async function handleSend() {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setError('');
    try {
      const req: AdminMessageRequest = { subject: subject.trim(), body: body.trim() };
      await apiPost(`/api/admin/users/${userId}/message`, req);
      setSent(true);
      setSubject('');
      setBody('');
      setTimeout(() => { setSent(false); setOpen(false); }, 2500);
    } catch (err: any) {
      setError(err.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  }

  function handleCancel() {
    setOpen(false);
    setSubject('');
    setBody('');
    setError('');
    setSent(false);
  }

  return (
    <div className="admin-message-section">
      <div className="admin-notes-header">
        <h4 className="admin-detail-heading" style={{ margin: 0 }}>Message User</h4>
        {!open && (
          <button className="btn-link" onClick={() => setOpen(true)}>+ Send message</button>
        )}
      </div>

      {sent && (
        <p className="admin-message-sent">Message sent to {userEmail}.</p>
      )}

      {open && !sent && (
        <div className="admin-message-composer">
          <input
            className="admin-notes-textarea"
            style={{ marginBottom: 8 }}
            placeholder="Subject"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            autoFocus
          />
          <textarea
            className="admin-notes-textarea"
            rows={5}
            placeholder="Your message…"
            value={body}
            onChange={e => setBody(e.target.value)}
          />
          <p className="admin-message-hint">Sent from support@cedarvaultapp.com — the user can reply directly to you.</p>
          {error && <div className="admin-error-msg" style={{ marginTop: 6 }}>{error}</div>}
          <div className="admin-notes-actions">
            <button
              className="admin-action-btn admin-action-btn--ok"
              onClick={handleSend}
              disabled={sending || !subject.trim() || !body.trim()}
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
            <button className="btn-link" onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Terms & Conditions Tab ───────────────────────────────────────────────────

function TermsTab() {
  const [versions, setVersions] = useState<TermsVersionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selected version for viewing content / audit
  const [selected, setSelected] = useState<TermsAndConditionsResponse | null>(null);
  const [acceptances, setAcceptances] = useState<TermsAcceptanceAuditEntry[] | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Create new version form
  const [showCreate, setShowCreate] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newEffective, setNewEffective] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    loadVersions();
  }, []);

  async function loadVersions() {
    setLoading(true);
    try {
      const data = await apiGet<TermsVersionSummary[]>('/api/admin/terms');
      setVersions(data);
    } catch {
      setError('Failed to load terms versions.');
    } finally {
      setLoading(false);
    }
  }

  async function selectVersion(id: string) {
    setSelected(null);
    setAcceptances(null);
    setLoadingDetail(true);
    try {
      const [detail, auditEntries] = await Promise.all([
        apiGet<TermsAndConditionsResponse>(`/api/admin/terms/${id}`),
        apiGet<TermsAcceptanceAuditEntry[]>(`/api/admin/terms/${id}/acceptances`),
      ]);
      setSelected(detail);
      setAcceptances(auditEntries);
    } catch {
      setError('Failed to load version details.');
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleCreate() {
    setCreateError('');
    if (!newVersion.trim()) { setCreateError('Version string is required.'); return; }
    if (!newContent.trim()) { setCreateError('Content is required.'); return; }
    if (!newEffective) { setCreateError('Effective date is required.'); return; }

    setCreating(true);
    try {
      const req: CreateTermsRequest = {
        version: newVersion.trim(),
        content: newContent.trim(),
        effectiveDateUtc: new Date(newEffective).toISOString(),
      };
      await apiPost('/api/admin/terms', req);
      setShowCreate(false);
      setNewVersion('');
      setNewContent('');
      setNewEffective('');
      await loadVersions();
    } catch (err: unknown) {
      setCreateError((err as Error).message || 'Failed to create version.');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string, version: string) {
    if (!window.confirm(`Delete draft v${version}? This cannot be undone.`)) return;
    try {
      await apiDelete(`/api/admin/terms/${id}`);
      if (selected?.id === id) setSelected(null);
      await loadVersions();
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to delete version.');
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="admin-terms">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Terms & Conditions</h2>
        <button className="btn btn-primary admin-action-btn" onClick={() => setShowCreate(v => !v)}>
          {showCreate ? 'Cancel' : '+ New Version'}
        </button>
      </div>

      {error && <div className="admin-error-msg">{error}</div>}

      {showCreate && (
        <div className="admin-terms-create">
          <h3 className="admin-terms-create-title">Create New Version</h3>
          {createError && <div className="admin-error-msg">{createError}</div>}
          <div className="admin-terms-create-row">
            <label className="admin-terms-label">Version string</label>
            <input
              className="admin-terms-input"
              placeholder="e.g. 1.1"
              value={newVersion}
              onChange={e => setNewVersion(e.target.value)}
            />
          </div>
          <div className="admin-terms-create-row">
            <label className="admin-terms-label">Effective date (UTC)</label>
            <input
              className="admin-terms-input"
              type="datetime-local"
              value={newEffective}
              onChange={e => setNewEffective(e.target.value)}
            />
          </div>
          <div className="admin-terms-create-row">
            <label className="admin-terms-label">Content (Markdown)</label>
            <textarea
              className="admin-terms-textarea"
              rows={16}
              placeholder="# Terms & Conditions&#10;&#10;Write your T&amp;Cs here in Markdown..."
              value={newContent}
              onChange={e => setNewContent(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Publishing…' : 'Publish Version'}
          </button>
        </div>
      )}

      {/* Version list */}
      <table className="admin-table admin-terms-table">
        <thead>
          <tr>
            <th>Version</th>
            <th>Effective Date</th>
            <th>Created</th>
            <th>Status</th>
            <th>Acceptances</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {versions.map(v => (
            <tr key={v.id} className={selected?.id === v.id ? 'admin-terms-row--selected' : ''}>
              <td><strong>v{v.version}</strong></td>
              <td>{formatDateTime(v.effectiveDate)}</td>
              <td>{formatDateTime(v.createdAt)}</td>
              <td>
                {v.isActive
                  ? <span className="admin-badge admin-badge--active">Active</span>
                  : new Date(v.effectiveDate) > new Date()
                    ? <span className="admin-badge admin-badge--pending">Draft</span>
                    : <span className="admin-badge admin-badge--inactive">Superseded</span>}
              </td>
              <td>{v.acceptanceCount.toLocaleString()}</td>
              <td className="admin-terms-actions">
                <button
                  className="btn-link"
                  onClick={() => selectVersion(v.id)}
                >
                  View
                </button>
                {new Date(v.effectiveDate) > new Date() && v.acceptanceCount === 0 && (
                  <button
                    className="btn-link btn-link--danger"
                    onClick={() => handleDelete(v.id, v.version)}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
          {versions.length === 0 && (
            <tr><td colSpan={6} className="admin-empty">No T&C versions found.</td></tr>
          )}
        </tbody>
      </table>

      {/* Detail panel */}
      {loadingDetail && <LoadingSpinner />}

      {selected && !loadingDetail && (
        <div className="admin-terms-detail">
          <h3 className="admin-terms-detail-title">v{selected.version} — Content</h3>
          <pre className="admin-terms-content-preview">{selected.content}</pre>

          <h3 className="admin-terms-detail-title" style={{ marginTop: '28px' }}>
            Acceptance Audit ({acceptances?.length ?? 0} records)
          </h3>
          {acceptances && acceptances.length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Accepted At</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>
                {acceptances.map((a, i) => (
                  <tr key={i}>
                    <td>{a.userName}</td>
                    <td>{a.userEmail}</td>
                    <td>{formatDateTime(a.acceptedAt)}</td>
                    <td>{a.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="admin-empty">No acceptances recorded for this version.</p>
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}
